import type { GameSnapshot, PlayerSnapshot } from "@games-monorepo/yak-trails-core";

export type SpectatorCycleDirection = "next" | "previous";

function leaderboardRanks(snapshot: GameSnapshot): Map<string, number> {
  return new Map(snapshot.leaderboard.map((entry) => [entry.id, entry.rank]));
}

/**
 * Returns active camera targets in a deterministic order. Team battles keep
 * surviving teammates together at the front; each group follows the
 * authoritative leaderboard order before falling back to stable player data.
 */
export function spectatorTargets(
  snapshot: GameSnapshot,
  selfPlayerId: string,
): PlayerSnapshot[] {
  const self = snapshot.players.find((player) => player.id === selfPlayerId);
  const ranks = leaderboardRanks(snapshot);

  return snapshot.players
    .filter((player) => player.id !== selfPlayerId && player.status === "active")
    .sort((left, right) => {
      if (snapshot.mode === "team" && self?.teamId) {
        const leftIsTeammate = left.teamId === self.teamId;
        const rightIsTeammate = right.teamId === self.teamId;
        if (leftIsTeammate !== rightIsTeammate) return leftIsTeammate ? -1 : 1;
      }

      return (ranks.get(left.id) ?? Number.MAX_SAFE_INTEGER)
        - (ranks.get(right.id) ?? Number.MAX_SAFE_INTEGER)
        || right.territoryPercent - left.territoryPercent
        || right.eliminations - left.eliminations
        || left.id.localeCompare(right.id);
    });
}

export function spectatorTargetIds(snapshot: GameSnapshot, selfPlayerId: string): string[] {
  return spectatorTargets(snapshot, selfPlayerId).map((player) => player.id);
}

/** Keeps a still-valid target, otherwise falls forward to the first candidate. */
export function resolveSpectatorTargetId(
  snapshot: GameSnapshot,
  selfPlayerId: string,
  currentTargetId?: string,
): string | undefined {
  const targets = spectatorTargetIds(snapshot, selfPlayerId);
  return currentTargetId && targets.includes(currentTargetId) ? currentTargetId : targets[0];
}

/** Cycles with wraparound and provides a deterministic entry when no target is selected. */
export function cycleSpectatorTargetId(
  snapshot: GameSnapshot,
  selfPlayerId: string,
  currentTargetId: string | undefined,
  direction: SpectatorCycleDirection,
): string | undefined {
  const targets = spectatorTargetIds(snapshot, selfPlayerId);
  if (targets.length === 0) return undefined;
  const currentIndex = currentTargetId ? targets.indexOf(currentTargetId) : -1;
  if (currentIndex < 0) return direction === "previous" ? targets.at(-1) : targets[0];
  const offset = direction === "next" ? 1 : -1;
  return targets[(currentIndex + offset + targets.length) % targets.length];
}
